import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import api from '../api'
import roles from '../helpers/userRoles'
import WS from '../helpers/gos_web_socket_client.js'

const FREE_BILLING_PLAN_ID = 2

Vue.use(Vuex)

const store = new Vuex.Store({
  state: {
    userInfo: null,
    token: '',
    appSettings: {
      sidebarHidden: false,
      footerBlockHidden: false,
      sortOptions: {
        column: null,
        direction: null
      },
      chatLastVisitTime: new Date(),
      activityVisitTime: new Date()
    },
    sectionsVisibility: {},
    orders: [],
    tasks: [],
    requests: [],
    calculatedCard: [],
    roles: roles
  },
  actions: {
    SET_TOKEN: function ({ commit }) {
      return new Promise((resolve, reject) => {
        api.get
          .login()
          .then(response => {
            commit('SET_TOKEN', {
              token: response.data.csrf_token
            })
            resolve()
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    LOGIN_USER: function ({ dispatch, commit, state }, creds) {
      return new Promise((resolve, reject) => {
        dispatch('SET_TOKEN')
          .then(() => {
            creds.token = state.token
            api.post
              .login(creds)
              .then(response => {
                commit('SET_USER_INFO', {
                  userInfo: response.data.data.user
                })
                resolve()
              })
              .catch(err => {
                reject(err)
              })
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    LOGOUT_USER: function ({ commit }) {
      return new Promise((resolve, reject) => {
        api.get
          .logout()
          .then(() => {
            commit('SET_USER_INFO', {
              userInfo: null
            })
            resolve()
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    REGISTER_USER: function ({ commit }, accountInfo) {
      return new Promise((resolve, reject) => {
        api.post
          .register(accountInfo)
          .then(() => {
            resolve()
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    CLEAR_USER_INFO: function ({ commit }) {
      commit('SET_USER_INFO', {
        userInfo: null
      })
    },
    TOGGLE_SIDEBAR: function ({ commit }) {
      commit('SET_SIDEBAR_VISIBILITY')
    },
    TOGGLE_FOOTER_BLOCK: function ({ commit }) {
      commit('SET_FOOTER_BLOCK_VISIBILITY')
    },
    UPDATE_CHAT_LAST_VISIT_TIME: ({ commit }, newTime) => {
      commit('SET_CHAT_LAST_VISIT_TIME', { newTime })
    },
    UPDATE_ACTIVITY_LAST_VISIT_TIME: ({ commit }, newTime) => {
      commit('SET_ACTIVITY_LAST_VISIT_TIME', { newTime })
    },
    ADD_ORDER: function ({ commit }, order) {
      commit('ADD_ORDER', { order })
    },
    EDIT_ORDER: function ({ commit }, order) {
      commit('EDIT_ORDER', { order })
    },
    DELETE_ORDER: function ({ commit }, orderId) {
      commit('DELETE_ORDER', { orderId })
    },
    CLEAN_ORDERS: function ({ commit }) {
      commit('CLEAN_ORDERS')
    },
    LOAD_ACTIVE_TASKS: function ({ commit }) {
      return new Promise((resolve, reject) => {
        api.get
          .activeTasks()
          .then(response => {
            const tasks = response.data.data.tasks.sort((x, y) => {
              return x.id - y.id
            })
            commit('CLEAR_FINISHED_TASKS')
            commit('SET_ACTIVE_TASKS', {
              tasks: tasks
            })
            resolve()
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    LOAD_OUTGOING_TASKS: function ({ commit }) {
      return new Promise((resolve, reject) => {
        api.get
          .outgoingTasks()
          .then(response => {
            const requests = response.data.data.requests.sort((x, y) => {
              return x.id - y.id
            })
            commit('SET_OUTGOING_TASKS', {
              requests: requests
            })
            resolve()
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    LOAD_TASKS: function ({ commit }) {
      return new Promise((resolve, reject) => {
        api.get
          .tasks()
          .then(response => {
            const tasks = response.data.data.tasks.sort((x, y) => {
              return x.id - y.id
            })
            commit('SET_TASKS', {
              tasks: tasks
            })
            resolve()
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    CONNECT_TO_WS: function ({ commit }) {
      const path =
        process.env.NODE_ENV === 'development'
          ? 'ws://' + location.hostname + ':8080'
          : 'wss://' + location.hostname
      const webSocket = WS.connect(path + '/ws')
      commit('STORE_WS_CONNECT', { webSocket })
    },
    UPDATE_TASK: function ({ commit }, taskId) {
      return new Promise((resolve, reject) => {
        api.get
          .task(taskId)
          .then(resp => {
            resp.data.data.task && commit('UPDATE_TASK_BY_ID', { task: resp.data.data.task })
            resolve()
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    UPDATE_REQUEST: function ({ commit }, requestId) {
      return new Promise((resolve, reject) => {
        api.get
          .request(requestId)
          .then(resp => {
            commit('UPDATE_REQUEST_BY_ID', { request: resp.data.data.request })
            resolve()
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    SAVE_TASKS_TABLE_SORT: function ({ commit }, sortOptions) {
      commit('SAVE_TASKS_TABLE_SORT', { sortOptions })
    },
    GET_USER_INFO: function ({ commit }) {
      api.get.getProfile().then(response => {
        commit('SET_USER_INFO', {
          userInfo: response.data.data.user
        })
      })
    },
    UPDATE_CARD: function ({ commit }) {
      return new Promise((resolve, reject) => {
        api.get.calculatedBasket()
          .then(resp => {
            commit('UPDATE_CARD', { card: resp.data.data.basket })
            resolve()
          })
          .catch(err => {
            reject(err)
          })
      })
    },
    ADD_TO_CARD: function ({ commit }, item) {
      commit('ADD_TO_CALCULATED_CARD', { item })
    },
    REMOVE_FROM_CARD: function ({ commit }, item) {
      commit('REMOVE_FROM_CALCULATED_CARD', { item })
    },
    CLEAR_CARD: function ({ commit }) {
      commit('CLEAR_CALCULATED_CARD')
    },
    SAVE_SECTION_VISIBILITY: function ({ commit }, item) {
      commit('SAVE_SECTION_VISIBILITY', { item })
    }
  },
  mutations: {
    SET_TOKEN: (state, { token }) => {
      state.token = token
    },
    SET_USER_INFO: (state, { userInfo }) => {
      state.userInfo = userInfo
    },
    SET_SIDEBAR_VISIBILITY: state => {
      state.appSettings.sidebarHidden = !state.appSettings.sidebarHidden
    },
    SET_FOOTER_BLOCK_VISIBILITY: state => {
      state.appSettings.footerBlockHidden = !state.appSettings.footerBlockHidden
    },
    SET_CHAT_LAST_VISIT_TIME: (state, { newTime }) => {
      state.appSettings.chatLastVisitTime = newTime
    },
    SET_ACTIVITY_LAST_VISIT_TIME: (state, { newTime }) => {
      state.appSettings.activityVisitTime = newTime
    },
    ADD_ORDER: (state, { order }) => {
      state.orders.push(order)
    },
    EDIT_ORDER: (state, { order }) => {
      const orderIndex = state.orders.findIndex(x => !!x && x.id === order.id)
      state.orders.splice(orderIndex, 1, order)
    },
    DELETE_ORDER: (state, { orderId }) => {
      const orderIndex = state.orders.findIndex(x => !!x && x.id === orderId)
      state.orders.splice(orderIndex, 1, null)
    },
    CLEAN_ORDERS: state => {
      state.orders = []
    },
    SET_TASKS: (state, { tasks }) => {
      tasks.forEach(newTask => {
        const isExistingTask = state.tasks.some(x => x.id === newTask.id)
        if (isExistingTask) {
          const existingTaskIndex = state.tasks.findIndex(
            x => x.id === newTask.id
          )
          state.tasks.splice(existingTaskIndex, 1, newTask)
        } else {
          state.tasks.push(newTask)
        }
      })
    },
    SET_ACTIVE_TASKS: (state, { tasks }) => {
      state.tasks = tasks
    },
    SET_OUTGOING_TASKS: (state, { requests }) => {
      state.requests = [...requests]
    },
    CLEAR_FINISHED_TASKS: (state) => {
      const oldActiveTasks = state.tasks.filter(x => x.status.name === 'finished')
      oldActiveTasks.forEach(oldTask => {
        const taskIndex = state.tasks.findIndex(x => x.id === oldTask.id)
        state.tasks.splice(taskIndex, 1)
      })

      state.tasks = state.tasks.filter(x => x.status.name !== 'finished')
    },
    STORE_WS_CONNECT: (state, { webSocket }) => {
      state.webSocket = webSocket
    },
    UPDATE_TASK_BY_ID: (state, { task }) => {
      const taskIndex = state.tasks.findIndex(x => x.id === task.id)
      const isNewTask = taskIndex < 0
      if (isNewTask) {
        state.tasks.push(task)
      } else {
        state.tasks.splice(taskIndex, 1, task)
      }
    },
    UPDATE_REQUEST_BY_ID: (state, { request }) => {
      const requestIndex = state.requests.findIndex(x => x.id === request.id)
      const isNewRequest = requestIndex < 0
      if (isNewRequest) {
        state.requests.push(request)
      } else {
        state.requests.splice(requestIndex, 1, request)
      }
    },
    SAVE_TASKS_TABLE_SORT: (state, { sortOptions }) => {
      state.appSettings.sortOptions = sortOptions
    },
    UPDATE_CARD: (state, { card }) => {
      state.calculatedCard = card
    },
    SAVE_SECTION_VISIBILITY: (state, { item }) => {
      state.sectionsVisibility = { ...state.sectionsVisibility, [item.id]: item.visible }
    }
  },
  getters: {
    isLoggedIn: state => {
      return !!state.userInfo
    },
    role: state => {
      return state.userInfo ? state.userInfo.roles : 'ROLE_USER'
    },
    activeOrders: state => {
      return state.orders.filter(x => !!x)
    },
    activeTasks: (state, getters) => {
      const activeTasks = state.tasks.filter(
        x => x.status.name === 'active' || x.status.name === 'new'
      )
      switch (getters.role) {
        case state.roles.director:
        case state.roles.manager:
        case state.roles.master:
          return activeTasks
        case state.roles.designer:
          return activeTasks.filter(x => x.designer.status.name !== 'unactive')
        case state.roles.printer:
          return activeTasks.filter(x => x.printer.status.name !== 'unactive')
        case state.roles.afterPrinter:
          return activeTasks.filter(
            x => x.after_printer.status.name !== 'unactive'
          )
        default:
          return []
      }
    },
    userTasks: (state, getters) => {
      switch (getters.role) {
        case state.roles.director:
          return state.tasks.filter(
            x =>
              (x.manager.user && x.manager.user.id === state.userInfo.id) ||
              x.status.name === 'stoped' || x.status.name === 'on stock' || x.status.name === 'contract'
          )
        case state.roles.manager:
          return state.tasks.filter(
            x => (x.manager.user && x.manager.user.id === state.userInfo.id) || x.status.name === 'on stock' || x.status.name === 'contract'
          )

        case state.roles.designer:
          return state.tasks.filter(
            x => x.designer.user && x.designer.user.id === state.userInfo.id
          )

        case state.roles.printer:
          return state.tasks.filter(
            x => x.printer.user && x.printer.user.id === state.userInfo.id
          )

        case state.roles.afterPrinter:
          return state.tasks.filter(
            x =>
              x.after_printer.user &&
              x.after_printer.user.id === state.userInfo.id
          )
        case state.roles.master:
          return state.tasks.filter(
            x =>
              (x.after_printer.user && x.after_printer.user.id === state.userInfo.id) ||
              (x.printer.user && x.printer.user.id === state.userInfo.id) ||
              (x.designer.user && x.designer.user.id === state.userInfo.id)
          )
        case state.roles.adAgent:
          return state.tasks.filter(x => x.status.name !== 'finished')
        default:
          return []
      }
    },
    availiableTasks: (state, getters) => {
      const availiableTasks = []
      return availiableTasks.concat(
        getters.activeTasks,
        getters.userPausedTasks,
        getters.userStoredTasks,
        getters.userContractedTasks,
        getters.archiveTasks,
        getters.userAcceptedTasks
      )
    },
    userActiveTasks: (state, getters) => {
      return getters.userTasks.filter(
        x => x.status.name === 'active' || x.status.name === 'new'
      )
    },
    userPausedTasks: (state, getters) => {
      return getters.userTasks.filter(x => x.status.name === 'stoped')
    },
    userStoredTasks: (state, getters) => {
      return getters.userTasks.filter(x => x.status.name === 'on stock')
    },
    userContractedTasks: (state, getters) => {
      return getters.userTasks.filter(x => x.status.name === 'contract')
    },
    userAcceptedTasks: (state, getters) => {
      return getters.userTasks
    },
    userOutgoingTasks: (state) => {
      return state.requests
    },
    archiveTasks: state => {
      return state.tasks.filter(x => x.status.name === 'finished')
    },
    tasksInDesign: (state, getters) => {
      return getters.activeTasks.filter(
        x => x.designer.status.name !== 'unactive'
      )
    },
    tasksInPrint: (state, getters) => {
      return getters.activeTasks.filter(
        x => x.printer.status.name !== 'unactive'
      )
    },
    tasksInAfterPrint: (state, getters) => {
      return getters.activeTasks.filter(
        x => x.after_printer.status.name !== 'unactive'
      )
    },
    tasksSortDirection: state => {
      return state.appSettings.sortOptions
        ? state.appSettings.sortOptions.direction
        : null
    },
    tasksSortColumn: (state, getters) => {
      return state.appSettings.sortOptions
        ? state.appSettings.sortOptions.column
        : null
    },
    sectionsVisibility: (state) => {
      return state.sectionsVisibility
    },
    isDocumentsAvailable (state) {
      // eslint-disable-next-line camelcase
      return state.userInfo?.customer?.billing_plan.id !== FREE_BILLING_PLAN_ID
    },
    isFinanceAvailable (state) {
      // eslint-disable-next-line camelcase
      return state.userInfo?.customer?.billing_plan.id !== FREE_BILLING_PLAN_ID
    }
  },
  plugins: [createPersistedState()]
})

export default store
